import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Group } from '@visx/group';
import { Pie } from '@visx/shape';
import { Text } from '@visx/text';
import { useTooltip, useTooltipInPortal } from '@visx/tooltip';
import { Crosshair, Database, Image, Map, Waypoints } from 'lucide-react';
import { FunctionComponent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface LayerTypeCount {
  type: string;
  count: number;
}

interface LayerStatsProps {
  layerTypeCounts: LayerTypeCount[];
  totalLayers: number;
}

const TYPE_CONFIG: Record<string, { color: string; icon: typeof Database; label: string }> = {
  POINTCLOUD: { color: '#6366f1', icon: Crosshair, label: 'dashboard.layers.pointcloud' },
  IMAGE: { color: '#f59e0b', icon: Image, label: 'dashboard.layers.image' },
  VECTOR: { color: '#22c55e', icon: Waypoints, label: 'dashboard.layers.vector' },
  MODEL: { color: '#8b5cf6', icon: Database, label: 'dashboard.layers.model' },
  TILE: { color: '#06b6d4', icon: Map, label: 'dashboard.layers.tile' },
  GROUP: { color: '#94a3b8', icon: Database, label: 'dashboard.layers.group' },
  WMS: { color: '#f43f5e', icon: Map, label: 'dashboard.layers.wms' },
};

const LayerStats: FunctionComponent<LayerStatsProps> = ({ layerTypeCounts, totalLayers }) => {
  const { t } = useTranslation('common');
  const { tooltipData, tooltipLeft, tooltipTop, tooltipOpen, showTooltip, hideTooltip } = useTooltip<LayerTypeCount>();
  const { containerRef, TooltipInPortal } = useTooltipInPortal({ detectBounds: true, scroll: true });

  const chartData = useMemo(() => {
    return layerTypeCounts.filter((d) => d.type !== 'GROUP').sort((a, b) => b.count - a.count);
  }, [layerTypeCounts]);

  const width = 160;
  const height = 160;
  const radius = Math.min(width, height) / 2;
  const innerRadius = radius - 36;

  return (
    <Card>
      <CardHeader>
        <CardTitle className='text-sm'>{t('dashboard.layers.title')}</CardTitle>
        <CardDescription>{t('dashboard.layers.description', { count: totalLayers })}</CardDescription>
      </CardHeader>
      <CardContent>
        {chartData.length > 0 && (
          <div className='flex items-start gap-6'>
            <div ref={containerRef} className='relative flex-shrink-0'>
              <svg width={width} height={height}>
                <Group top={height / 2} left={width / 2}>
                  <Pie
                    data={chartData}
                    pieValue={(d) => d.count}
                    outerRadius={radius}
                    innerRadius={innerRadius}
                    cornerRadius={3}
                    padAngle={0.02}>
                    {(pie) =>
                      pie.arcs.map((arc, i) => {
                        const config = TYPE_CONFIG[arc.data.type];
                        return (
                          <g
                            key={`arc-${i}`}
                            onMouseMove={(e) => {
                              const rect = (e.currentTarget.ownerSVGElement as SVGSVGElement).getBoundingClientRect();
                              showTooltip({
                                tooltipData: arc.data,
                                tooltipLeft: e.clientX - rect.left,
                                tooltipTop: e.clientY - rect.top,
                              });
                            }}
                            onMouseLeave={hideTooltip}>
                            <path
                              d={pie.path(arc) || ''}
                              fill={config?.color || '#94a3b8'}
                              className='transition-opacity hover:opacity-80 cursor-pointer'
                            />
                          </g>
                        );
                      })
                    }
                  </Pie>
                  <Text
                    textAnchor='middle'
                    verticalAnchor='middle'
                    dy={-8}
                    className='fill-slate-900 font-medium dark:fill-white text-lg font-bold'>
                    {totalLayers}
                  </Text>
                  <Text
                    textAnchor='middle'
                    verticalAnchor='middle'
                    dy={12}
                    className='fill-slate-500 font-medium dark:fill-stone-400 text-[11px] font-medium'>
                    {t('dashboard.layers.totalLabel')}
                  </Text>
                </Group>
              </svg>
              {tooltipOpen && tooltipData && (
                <TooltipInPortal
                  top={tooltipTop}
                  left={tooltipLeft}
                  className='!bg-slate-800 !text-white !rounded-md !px-3 !py-2 !text-xs !shadow-lg'>
                  <div className='font-semibold'>
                    {t((TYPE_CONFIG[tooltipData.type]?.label as any) || tooltipData.type)}
                  </div>
                  <div className='text-slate-300'>{t('dashboard.layers.layerCount', { count: tooltipData.count })}</div>
                </TooltipInPortal>
              )}
            </div>
            <div className='flex-1 min-w-0 space-y-1.5'>
              {chartData.map((d) => {
                const config = TYPE_CONFIG[d.type];
                const pct = totalLayers > 0 ? Math.round((d.count / totalLayers) * 100) : 0;
                return (
                  <div key={d.type} className='flex items-center gap-2 text-xs'>
                    <div
                      className='size-2.5 rounded-full flex-shrink-0'
                      style={{ background: config?.color || '#94a3b8' }}
                    />
                    <span className='flex-1 text-slate-600 dark:text-stone-300 font-medium truncate'>
                      {t((config?.label as any) || d.type)}
                    </span>
                    <span className='font-mono font-medium text-slate-500 dark:text-stone-400 flex-shrink-0 w-6 text-right'>
                      {d.count}
                    </span>
                    <span className='font-mono font-medium text-slate-400 dark:text-stone-500 flex-shrink-0 w-8 text-right'>
                      {pct}%
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {chartData.length === 0 && (
          <div className='text-center py-8 text-sm text-slate-400 dark:text-stone-500'>
            {t('dashboard.layers.noData')}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default LayerStats;
